/**
 * Fetch wrapper that intercepts 401 and 403 (needs legal) responses.
 * - 401: for kiosk sessions, try device refresh once then retry; else logout
 * - 403 with needsLegalAcceptance: triggers TOS gate to show
 */

import { API_BASE_URL } from './apiBaseUrl'

const TOKEN_KEY = 'lts_jwt_token'
const KIOSK_REFRESH_KEY = 'lts_kiosk_refresh_token'

export const UNAUTHORIZED_EVENT = 'lts:unauthorized'
export const NEEDS_LEGAL_ACCEPTANCE_EVENT = 'lts:needsLegalAcceptance'

let kioskRefreshInFlight: Promise<boolean> | null = null

async function tryKioskDeviceRefresh(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  const refreshToken = localStorage.getItem(KIOSK_REFRESH_KEY)
  if (!refreshToken) return false

  if (!kioskRefreshInFlight) {
    kioskRefreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/kiosk/device/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ refreshToken }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || !data?.success || !data.accessToken || !data.refreshToken) return false
        localStorage.setItem(TOKEN_KEY, String(data.accessToken))
        localStorage.setItem(KIOSK_REFRESH_KEY, String(data.refreshToken))
        return true
      } catch {
        return false
      } finally {
        kioskRefreshInFlight = null
      }
    })()
  }
  return kioskRefreshInFlight
}

function withAuthorization(init: RequestInit | undefined, accessToken: string): RequestInit {
  const headers = new Headers(init?.headers || {})
  headers.set('Authorization', `Bearer ${accessToken}`)
  return { ...init, headers }
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const res = await fetch(input, init)

  if (res.status === 401) {
    const hasKioskRefresh =
      typeof window !== 'undefined' && Boolean(localStorage.getItem(KIOSK_REFRESH_KEY))
    if (hasKioskRefresh) {
      const refreshed = await tryKioskDeviceRefresh()
      if (refreshed) {
        const token = localStorage.getItem(TOKEN_KEY)
        if (token) {
          return fetch(input, withAuthorization(init, token))
        }
      }
    }

    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(KIOSK_REFRESH_KEY)
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT))
    return res
  }

  if (res.status === 403) {
    try {
      const clone = res.clone()
      const data = await clone.json()
      if (data?.needsLegalAcceptance) {
        window.dispatchEvent(new CustomEvent(NEEDS_LEGAL_ACCEPTANCE_EVENT))
      }
    } catch {
      // Ignore parse errors
    }
  }

  return res
}
